import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useOrgTheme } from '../../context/OrgThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { API_URL } from '../../constants/appConstants';
import { IntranetProject } from '../../types';
import { useCurrentTasks } from '../../hooks/useCurrentTasks';
import { useBambooStatus } from '../../hooks/useBambooStatus';
import { useClockSessions } from '../../hooks/useClockSessions';
import { useIdleWarning } from '../../hooks/useIdleWarning';
import { ADMIN_NAMES } from '../../config/teamConfig';
import {
  formatTime,
  getGreeting,
  getCurrentDate,
  getStatusColor,
  getProgressWidth
} from '../../utils/dashboardUtils';
import TimeTracker from '../time-tracker/TimeTracker';
import IdleWarningModal from '../common/IdleWarningModal';
import './Dashboard.css';

interface ProjectContributorTask {
  id: string;
  description: string;
  hours: number;
  status: string;
  type: string;
}

interface ProjectContributor {
  name: string;
  hours: number;
  taskCount: number;
  tasks: ProjectContributorTask[];
}

interface ProjectContributorSummary {
  projectId: string;
  totalHours: number;
  taskCount: number;
  contributors: ProjectContributor[];
}

interface AiModelOption {
  id: string;
  name: string;
}

interface AiEvidenceTask {
  taskId: string;
  hours: number;
  reason: string;
  confidence?: string;
  description?: string;
}

interface AiEfficiencyDimension {
  score?: number;
  reasoning?: string;
}

interface AiAnalysis {
  summary?: string;
  efficiencyAssessment?: string;
  riskLevel?: 'low' | 'medium' | 'high' | string;
  timePaddingAssessment?: string;
  efficiencyScore?: number;
  timePaddingRiskScore?: number;
  efficiencyBreakdown?: Record<string, AiEfficiencyDimension>;
  riskFlags?: string[];
  timePaddingEvidence?: AiEvidenceTask[];
  positiveSignals?: string[];
  evidenceTasks?: AiEvidenceTask[];
  questionsForManager?: string[];
  confidence?: 'low' | 'medium' | 'high' | string;
  caution?: string;
}

interface AiAnalysisResponse {
  model: string;
  analysis: AiAnalysis | null;
  raw: string | null;
}

interface ContributorAnalysisState {
  loading: boolean;
  error: string | null;
  result: AiAnalysisResponse | null;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { themeOrg } = useOrgTheme();
  const isManager = user?.username
    ? ADMIN_NAMES.includes(user.username.toLowerCase().trim())
    : false;
  const { setCurrentEmployeeId } = useNotifications();
  const [projects, setProjects] = useState<IntranetProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<IntranetProject | null>(null);
  const [projectContributorSummary, setProjectContributorSummary] = useState<ProjectContributorSummary | null>(null);
  const [loadingProjectContributors, setLoadingProjectContributors] = useState(false);
  const [projectContributorError, setProjectContributorError] = useState<string | null>(null);
  const [expandedContributorName, setExpandedContributorName] = useState<string | null>(null);
  const [aiModels, setAiModels] = useState<AiModelOption[]>([]);
  const [selectedAiModel, setSelectedAiModel] = useState('');
  const [loadingAiModels, setLoadingAiModels] = useState(false);
  const [aiModelError, setAiModelError] = useState<string | null>(null);
  const [aiAnalysisByContributor, setAiAnalysisByContributor] = useState<Record<string, ContributorAnalysisState>>({});
  const projectContributorRequestIdRef = React.useRef(0);
  
  // Get employee ID and tasks from current tasks hook
  const { user: intranetUser, tasks: currentTasks } = useCurrentTasks();
  
  // Set the current employee ID for task inquiry notifications
  useEffect(() => {
    if (intranetUser?.id) {
      setCurrentEmployeeId(intranetUser.id);
    }
  }, [intranetUser?.id, setCurrentEmployeeId]);
  
  // Bamboo clock status
  const { 
    status: bambooStatus, 
    loading: bambooLoading, 
    linked: bambooLinked,
    clockIn: bambooClockIn, 
    clockOut: bambooClockOut, 
    elapsedSeconds: bambooElapsed,
    todayTotalSeconds: bambooTodayTotal
  } = useBambooStatus(intranetUser?.id || null);
  
  // Clock session tracking
  const {
    handleClockIn,
    handleClockOut,
    showSessionHistory,
    setShowSessionHistory
  } = useClockSessions({
    isClockedIn: bambooStatus?.isClockedIn || false,
    elapsedSeconds: bambooElapsed,
    onClockIn: bambooClockIn,
    onClockOut: bambooClockOut
  });

  // Idle warning
  const {
    showIdleWarning,
    dismissWarning,
    idleMinutes
  } = useIdleWarning({
    isClockedIn: bambooStatus?.isClockedIn || false,
    elapsedSeconds: bambooElapsed,
    currentTasks,
    intranetUser,
    username: user?.username
  });
  
  // Timer state (lifted from TimeTracker)
  const [isTracking, setIsTracking] = useState(false);
  const [, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/intranet/projects`, { withCredentials: true });
        setProjects(response.data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching projects:', err);
        setError(err.response?.data?.error || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isTracking && startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTracking, startTime]);

  // Handle starting a task from the modal
  const handleStartTaskFromModal = useCallback(() => {
    dismissWarning();
    const tasksSection = document.querySelector('.current-tasks-panel');
    if (tasksSection) {
      tasksSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [dismissWarning]);

  // Callbacks for TimeTracker
  const handleTimerStart = useCallback((time: Date) => {
    setIsTracking(true);
    setStartTime(time);
    setElapsedTime(0);
  }, []);

  const handleTimerStop = useCallback(() => {
    setIsTracking(false);
    setStartTime(null);
    setElapsedTime(0);
  }, []);

  const handleCloseProjectModal = useCallback(() => {
    projectContributorRequestIdRef.current += 1;
    setSelectedProject(null);
    setProjectContributorSummary(null);
    setProjectContributorError(null);
    setLoadingProjectContributors(false);
    setExpandedContributorName(null);
    setAiAnalysisByContributor({});
  }, []);

  const handleOpenProjectModal = useCallback(async (project: IntranetProject) => {
    const requestId = projectContributorRequestIdRef.current + 1;
    projectContributorRequestIdRef.current = requestId;
    setSelectedProject(project);
    setProjectContributorSummary(null);
    setProjectContributorError(null);
    setLoadingProjectContributors(true);
    setExpandedContributorName(null);
    setAiAnalysisByContributor({});

    try {
      const response = await axios.get<ProjectContributorSummary>(
        `${API_URL}/intranet/projects/${project.id}/contributors`,
        { withCredentials: true }
      );
      if (projectContributorRequestIdRef.current !== requestId) return;
      setProjectContributorSummary(response.data);
    } catch (err: any) {
      if (projectContributorRequestIdRef.current !== requestId) return;
      console.error('Error fetching project contributor hours:', err);
      setProjectContributorError(
        err.response?.data?.error || 'Failed to load contributor hours'
      );
    } finally {
      if (projectContributorRequestIdRef.current === requestId) {
        setLoadingProjectContributors(false);
      }
    }
  }, []);

  const handleToggleContributorTasks = useCallback((contributorName: string) => {
    setExpandedContributorName(currentName =>
      currentName === contributorName ? null : contributorName
    );
  }, []);

  useEffect(() => {
    if (!isManager || !selectedProject || aiModels.length > 0 || loadingAiModels) return;

    const fetchAiModels = async () => {
      setLoadingAiModels(true);
      setAiModelError(null);

      try {
        const response = await axios.get<{ configured: boolean; models: AiModelOption[] }>(
          `${API_URL}/ai/models`,
          { withCredentials: true }
        );
        setAiModels(response.data.models || []);
        setSelectedAiModel(currentModel => currentModel || response.data.models?.[0]?.id || '');
        if (!response.data.configured) {
          setAiModelError('OpenRouter is not configured. Set OPENROUTER_API_KEY on the backend.');
        }
      } catch (err: any) {
        setAiModelError(err.response?.data?.error || 'Failed to load AI models');
      } finally {
        setLoadingAiModels(false);
      }
    };

    fetchAiModels();
  }, [isManager, selectedProject, aiModels.length, loadingAiModels]);

  const handleAnalyzeContributor = useCallback(async (contributor: ProjectContributor) => {
    if (!isManager || !selectedProject || !projectContributorSummary || !selectedAiModel) return;

    setAiAnalysisByContributor(prev => ({
      ...prev,
      [contributor.name]: { loading: true, error: null, result: null }
    }));

    try {
      const response = await axios.post<AiAnalysisResponse>(
        `${API_URL}/ai/project-contributor-analysis`,
        {
          model: selectedAiModel,
          project: {
            id: selectedProject.id,
            name: selectedProject.name,
            totalHours: projectContributorSummary.totalHours || selectedProject.taskHours,
            budgetHours: selectedProject.paidHours,
            taskCount: projectContributorSummary.taskCount
          },
          contributor,
          contributors: projectContributorSummary.contributors
        },
        { withCredentials: true }
      );

      setAiAnalysisByContributor(prev => ({
        ...prev,
        [contributor.name]: { loading: false, error: null, result: response.data }
      }));
    } catch (err: any) {
      setAiAnalysisByContributor(prev => ({
        ...prev,
        [contributor.name]: {
          loading: false,
          error: err.response?.data?.error || 'Failed to analyze contributor',
          result: null
        }
      }));
    }
  }, [isManager, selectedProject, projectContributorSummary, selectedAiModel]);

  const renderAnalysisList = (title: string, items?: string[]) => {
    if (!items || items.length === 0) return null;

    return (
      <div className="project-ai-section">
        <span className="project-ai-section-title">{title}</span>
        <ul>
          {items.map((item, index) => (
            <li key={`${title}-${index}`}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  const formatAnalysisLabel = (value: string) => {
    return value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const formatScore = (score: number): string => {
    return Number(score).toFixed(1);
  };

  const renderContributorAnalysis = (contributor: ProjectContributor, state?: ContributorAnalysisState) => {
    if (!state) return null;

    if (state.loading) {
      return (
        <div className="project-ai-result project-ai-loading">
          Analyzing time patterns...
        </div>
      );
    }

    if (state.error) {
      return <div className="project-ai-result project-ai-error">{state.error}</div>;
    }

    if (!state.result) return null;

    const analysis = state.result.analysis;
    if (!analysis) {
      return (
        <div className="project-ai-result">
          <span className="project-ai-section-title">AI response</span>
          <p>{state.result.raw || 'No analysis returned.'}</p>
        </div>
      );
    }

    const evidenceTasks = (analysis.timePaddingEvidence && analysis.timePaddingEvidence.length > 0)
      ? analysis.timePaddingEvidence
      : analysis.evidenceTasks;
    const verdict = analysis.summary || analysis.efficiencyAssessment || 'No summary returned.';
    const getTaskDescription = (task: AiEvidenceTask) => {
      const matchingTask = contributor.tasks.find(contributorTask =>
        contributorTask.id === task.taskId || String(contributorTask.id) === String(task.taskId)
      );

      return task.description || matchingTask?.description || '';
    };

    return (
      <div className="project-ai-result">
        <div className="project-ai-verdict">
          {analysis.efficiencyScore !== undefined && (
            <span>Efficiency: <strong>{formatScore(analysis.efficiencyScore)}/100</strong></span>
          )}
          {analysis.timePaddingRiskScore !== undefined && (
            <span>Padding risk: <strong>{formatScore(analysis.timePaddingRiskScore)}/100</strong></span>
          )}
        </div>

        <p className="project-ai-summary">{verdict}</p>

        {analysis.efficiencyBreakdown && Object.keys(analysis.efficiencyBreakdown).length > 0 && (
          <div className="project-ai-section">
            <span className="project-ai-section-title">Efficiency breakdown</span>
            <div className="project-ai-breakdown-grid">
              {Object.entries(analysis.efficiencyBreakdown)
                .filter(([key]) => key !== 'taskGranularity')
                .map(([key, dimension]) => (
                  <div key={key} className="project-ai-breakdown-card">
                    <div>
                      <span>{formatAnalysisLabel(key)}</span>
                      {dimension.score !== undefined && <strong>{formatScore(dimension.score)}/100</strong>}
                    </div>
                    {dimension.reasoning && <p>{dimension.reasoning}</p>}
                  </div>
                ))}
            </div>
          </div>
        )}

        {renderAnalysisList('Signals to review', analysis.riskFlags)}

        {evidenceTasks && evidenceTasks.length > 0 && (
          <div className="project-ai-section">
            <span className="project-ai-section-title">Task evidence</span>
            {evidenceTasks.map((task, index) => (
              <div key={`${task.taskId}-${index}`} className="project-ai-evidence-task">
                <strong>
                  #{task.taskId} · {Number(task.hours || 0).toFixed(2)}h
                  {task.confidence ? ` · ${formatAnalysisLabel(task.confidence)} confidence` : ''}
                </strong>
                {getTaskDescription(task) && (
                  <p className="project-ai-evidence-description">{getTaskDescription(task)}</p>
                )}
                <span>{task.reason}</span>
              </div>
            ))}
          </div>
        )}

        {renderAnalysisList('Positive signals', analysis.positiveSignals)}
        {renderAnalysisList('Questions for manager', analysis.questionsForManager)}
      </div>
    );
  };

  useEffect(() => {
    if (!selectedProject) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseProjectModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedProject, handleCloseProjectModal]);

  // All projects from the intranet are already "active" — show them all
  const activeProjects = projects;

  return (
    <div className="dashboard">
      {/* Idle Warning Modal */}
      <IdleWarningModal
        isOpen={showIdleWarning}
        onClose={dismissWarning}
        onStartTask={handleStartTaskFromModal}
        idleMinutes={idleMinutes}
      />

      {selectedProject && (
        <div className="project-hours-modal-overlay" onClick={handleCloseProjectModal}>
          <div
            className="project-hours-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-hours-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="project-hours-modal-header">
              <div>
                <span className="project-hours-modal-eyebrow">Project Hours</span>
                <h3 id="project-hours-title">{selectedProject.name}</h3>
              </div>
              <button
                type="button"
                className="project-hours-modal-close"
                onClick={handleCloseProjectModal}
                aria-label="Close project hours"
              >
                ×
              </button>
            </div>

            <div className="project-hours-summary-grid">
              <div className="project-hours-summary-card">
                <span>Total Used</span>
                <strong>{selectedProject.taskHours.toFixed(2)}h</strong>
              </div>
              <div className="project-hours-summary-card">
                <span>Budget</span>
                <strong>{selectedProject.paidHours > 0 ? `${selectedProject.paidHours.toFixed(2)}h` : 'Uncapped'}</strong>
              </div>
              <div className="project-hours-summary-card">
                <span>Contributors</span>
                <strong>{projectContributorSummary?.contributors.length ?? '...'}</strong>
              </div>
            </div>

            {isManager && (
              <>
                <div className="project-ai-toolbar">
                  <div>
                    <span className="project-ai-toolbar-label">AI model</span>
                    <select
                      value={selectedAiModel}
                      onChange={(event) => setSelectedAiModel(event.target.value)}
                      disabled={loadingAiModels || aiModels.length === 0}
                    >
                      {loadingAiModels ? (
                        <option value="">Loading models...</option>
                      ) : aiModels.length === 0 ? (
                        <option value="">No models available</option>
                      ) : (
                        aiModels.map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <p>
                    AI results are review indicators. Verify the task evidence before making decisions.
                  </p>
                </div>
                {aiModelError && <div className="project-ai-model-error">{aiModelError}</div>}
              </>
            )}

            {loadingProjectContributors ? (
              <div className="project-hours-loading">
                <div className="loading-spinner"></div>
                <span>Loading contributor hours...</span>
              </div>
            ) : projectContributorError ? (
              <div className="project-hours-error">
                <span>{projectContributorError}</span>
              </div>
            ) : !projectContributorSummary || projectContributorSummary.contributors.length === 0 ? (
              <div className="project-hours-empty">
                No contributor breakdown was returned for this project.
              </div>
            ) : (
              <div className="project-contributors-list">
                {projectContributorSummary.contributors.map((contributor) => {
                  const totalHours = projectContributorSummary.totalHours || selectedProject.taskHours || 1;
                  const width = Math.min(100, (contributor.hours / totalHours) * 100);
                  const isExpanded = expandedContributorName === contributor.name;

                  return (
                    <div key={contributor.name} className={`project-contributor-card ${isExpanded ? 'expanded' : ''}`}>
                      <button
                        type="button"
                        className="project-contributor-row"
                        onClick={() => handleToggleContributorTasks(contributor.name)}
                        aria-expanded={isExpanded}
                      >
                        <div className="project-contributor-info">
                          <span className="project-contributor-name">{contributor.name}</span>
                          <span className="project-contributor-meta">
                            {contributor.taskCount} {contributor.taskCount === 1 ? 'task' : 'tasks'}
                          </span>
                        </div>
                        <div className="project-contributor-hours">
                          <strong>{contributor.hours.toFixed(2)}h</strong>
                          <div className="project-contributor-bar">
                            <div style={{ width: `${width}%` }}></div>
                          </div>
                        </div>
                        <span className="project-contributor-chevron" aria-hidden="true">
                          {isExpanded ? '−' : '+'}
                        </span>
                      </button>

                      {isExpanded && (
                        <div className="project-contributor-tasks">
                          {isManager && (
                            <>
                              <div className="project-contributor-ai-actions">
                                <button
                                  type="button"
                                  className="project-ai-analyze-btn"
                                  onClick={() => handleAnalyzeContributor(contributor)}
                                  disabled={
                                    !selectedAiModel ||
                                    Boolean(aiModelError) ||
                                    aiAnalysisByContributor[contributor.name]?.loading
                                  }
                                >
                                  {aiAnalysisByContributor[contributor.name]?.loading
                                    ? 'Analyzing...'
                                    : 'Analyze efficiency'}
                                </button>
                              </div>

                              {renderContributorAnalysis(contributor, aiAnalysisByContributor[contributor.name])}
                            </>
                          )}

                          {contributor.tasks.length === 0 ? (
                            <div className="project-contributor-task-empty">
                              No tasks found for this contributor.
                            </div>
                          ) : (
                            contributor.tasks.map((task) => (
                              <div key={task.id} className="project-contributor-task">
                                <div className="project-contributor-task-main">
                                  <span className="project-contributor-task-title">
                                    {task.description || `${task.type || 'Project'} task`}
                                  </span>
                                  <span className="project-contributor-task-meta">
                                    #{task.id}
                                    {task.type ? ` · ${task.type}` : ''}
                                    {task.status ? ` · ${task.status}` : ''}
                                  </span>
                                </div>
                                <strong>{task.hours.toFixed(2)}h</strong>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
      
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1 className="welcome-title">
            {getGreeting()}, <span className="user-highlight">{user?.username}</span>
          </h1>
          <p className="welcome-date">{getCurrentDate()}</p>
        </div>
        <div className="header-timer">
          <div className={`header-timer-display ${bambooStatus?.isClockedIn ? 'active' : ''}`}>
            {formatTime(bambooTodayTotal)}
          </div>
          
          {/* Show fallback only when backend confirms Bamboo is NOT linked */}
          {!bambooLoading && bambooStatus && !bambooLinked ? (
            <div className="header-timer-controls bamboo-fallback">
              <div className="header-timer-indicator bamboo-unavailable">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14, marginRight: 4 }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                BambooHR Not Linked
              </div>
              <a 
                href="https://elinkdesign.bamboohr.com/employees/timesheet/"
                target="_blank"
                rel="noopener noreferrer"
                className="header-clock-btn bamboo-link"
                title="Open BambooHR to clock in/out"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Open BambooHR
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12, marginLeft: 4 }}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>
          ) : (
            // Normal clock in/out controls
            <div className="header-timer-controls">
              {bambooStatus?.isClockedIn ? (
                <div className="header-timer-indicator">
                  <span className="pulse"></span>
                  Clocked In
                </div>
              ) : (
                <div className="header-timer-indicator clocked-out">
                  Not Clocked In
                </div>
              )}
              <button
                className={`header-clock-btn ${bambooStatus?.isClockedIn ? 'clock-out' : 'clock-in'}`}
                onClick={() => bambooStatus?.isClockedIn ? handleClockOut() : handleClockIn()}
                disabled={bambooLoading}
                title={bambooStatus?.isClockedIn ? 'Clock Out' : 'Clock In'}
              >
                {bambooLoading ? (
                  <div className="btn-spinner"></div>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {bambooStatus?.isClockedIn ? 'Clock Out' : 'Clock In'}
                  </>
                )}
              </button>
              <button
                className={`header-history-btn ${showSessionHistory ? 'active' : ''}`}
                onClick={() => setShowSessionHistory(!showSessionHistory)}
                title="View clock history"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 8v4l3 3"></path>
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
                {bambooStatus?.entries && bambooStatus.entries.length > 0 && (
                  <span className="history-badge">{bambooStatus.entries.length}</span>
                )}
              </button>
            </div>
          )}
          
          {/* Session History Dropdown - Shows entries from Bamboo */}
          {showSessionHistory && (
            <div className="session-history-panel">
              <div className="session-history-header">
                <h3>Today's Sessions</h3>
                <span className="session-total">Total: {formatTime(bambooTodayTotal)}</span>
              </div>
              {(!bambooStatus?.entries || bambooStatus.entries.length === 0) ? (
                <div className="session-empty">
                  No sessions recorded today
                  <a 
                    href="https://elinkdesign.bamboohr.com/employees/timesheet/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bamboo-direct-link"
                  >
                    Manage time in BambooHR →
                  </a>
                </div>
              ) : (
                <div className="session-list">
                  {bambooStatus.entries.map((entry, index) => (
                    <div key={index} className={`session-item ${entry.isActive ? 'current' : ''}`}>
                      <div className="session-times">
                        <span className="session-in">{entry.startTime}</span>
                        <span className="session-arrow">→</span>
                        <span className={`session-out ${entry.isActive ? 'active' : ''}`}>
                          {entry.isActive ? 'Now' : entry.endTime}
                        </span>
                      </div>
                      <div className="session-duration">
                        {entry.isActive ? formatTime(bambooElapsed) : formatTime(entry.duration)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Time Tracker - Full Width */}
      <div className="dashboard-card time-tracker-card-wide">
        <div className="card-header">
          <div className="card-icon time-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <h2 className="card-title">Time Tracker</h2>
        </div>
        <div className="card-content">
          <TimeTracker
            onTimerStart={handleTimerStart}
            onTimerStop={handleTimerStop}
          />
        </div>
      </div>

      {/* Projects Section - Full Width */}
      <div className="dashboard-card projects-card-wide">
        <div className="card-header">
          <div className="card-icon projects-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h2 className="card-title">Active Projects</h2>
          <span className="card-badge">{activeProjects.length}</span>
        </div>
        <div className="card-content">
          {loading ? (
            <div className="projects-loading">
              <div className="loading-spinner"></div>
              <span>Loading projects...</span>
            </div>
          ) : error ? (
            <div className="projects-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          ) : (
            <>
              {/* All Active Projects Grid */}
              <div className="projects-section">
                <div className="projects-grid">
                  {activeProjects.length === 0 ? (
                    <div className="projects-empty">
                      <p>No active projects</p>
                    </div>
                  ) : (
                    activeProjects.map(project => {
                      const remaining = project.paidHours - project.taskHours;
                      const isOverBudget = remaining < 0;
                      const isLowBudget = remaining > 0 && remaining < 5;
                      
                      return (
                        <button
                          type="button"
                          key={project.id} 
                          className={`project-cell status-${project.status}`}
                          onClick={() => handleOpenProjectModal(project)}
                          aria-label={`View contributor hours for ${project.name}`}
                        >
                          {/* Status Indicator Dot */}
                          <div className="cell-status-indicator"></div>
                          
                          <div className="cell-content">
                            {/* Project Name */}
                            <span className="cell-name">{project.name}</span>
                            
                            {/* Hours Display */}
                            <div className="cell-hours-row">
                              <div className="cell-hours-main">
                                <span 
                                  className="cell-hours-used" 
                                  style={{ color: getStatusColor(project.status, themeOrg) }}
                                >
                                  {project.taskHours.toFixed(1)}
                                </span>
                                <span className="cell-hours-separator">/</span>
                                <span className="cell-hours-total">
                                  {project.paidHours > 0 ? project.paidHours : '∞'}
                                </span>
                              </div>
                              {project.paidHours > 0 && (
                                <span 
                                  className="cell-percent"
                                  style={{ 
                                    color: getStatusColor(project.status, themeOrg),
                                    background: `${getStatusColor(project.status, themeOrg)}20`
                                  }}
                                >
                                  {project.percentUsed}%
                                </span>
                              )}
                            </div>
                            
                            {/* Progress Bar */}
                            <div className="cell-progress">
                              <div 
                                className="cell-progress-bar"
                                style={{ 
                                  width: `${getProgressWidth(project)}%`,
                                  background: `linear-gradient(90deg, ${getStatusColor(project.status, themeOrg)}, ${getStatusColor(project.status, themeOrg)}cc)`
                                }}
                              ></div>
                            </div>
                            
                            {/* Remaining Hours */}
                            {project.paidHours > 0 && (
                              <div className={`cell-remaining ${isOverBudget ? 'over' : isLowBudget ? 'low' : ''}`}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                <span>
                                  {isOverBudget 
                                    ? `${Math.abs(remaining).toFixed(1)}h over` 
                                    : `${remaining.toFixed(1)}h left`
                                  }
                                </span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
